import { call, put, takeLatest } from 'redux-saga/effects';
import Api, { Mapper } from '../../api';
import {
  createLedgerAsync,
  createNoteAsync,
  requestLedgersAsync,
  archiveLedgerAsync,
  unArchiveLedgerAsync,
  cancelLedgerAsync,
  importLedgersAsync,
  importLedgersTemplateAsync,
  exportLedgersAsync,
  requestLedgerHistoryAsync,
  editLedgerAsync,
  requestLedgerNotesAsync,
  updateOvernightPaymentAsync,
} from './actions';
import { createRequestSaga } from '@/redux/helpers';
import { prepareFileForRequest } from '@/lib/utils/files';
import { addToastsAction } from '@/redux/toasts';
import { mapErrorToastsData } from '@/lib/api';
import { toastType } from '@/components/common';

function* requestLedgers({ payload }){
  const response = yield call(
    Api.getLedgers,
    { ...payload },
  );

  yield put(requestLedgersAsync.success(Mapper.getLedgers(response)));
}

function* archiveLedger({ payload }){
  try {
    const { ledgerId, successCallback } = payload;
    yield call(
      Api.archiveLedger(ledgerId),
      { ...payload },
    );

    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: 'Archived Ledger! Your Ledger has been archived.',
      details: null,
    }]));

    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* unArchiveLedger({ payload }){
  try {
    const { ledgerId, successCallback } = payload;
    yield call(
      Api.unArchiveLedger(ledgerId),
      { ...payload },
    );

    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: 'Unarchived Ledger! Your Ledger has been unarchived.',
      details: null,
    }]));

    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* cancelLedger({ payload }){
  try {
    const { ledgerId, successCallback } = payload;
    yield call(
      Api.cancelLedger(ledgerId),
      { ...payload },
    );

    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: 'Canceled Ledger.',
      details: null,
    }]));

    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* getLedgerHisotry({ payload }){
  const response = yield call(
    Api.getLedgerHistory(payload.ledgerId),
    { ...payload },
  );

  yield put(requestLedgerHistoryAsync.success(Mapper.getLedgerHistory(response)));
}

function* importLedgersSaga({ payload }) {
  try {
    const { file, format, recruitingSeasonId, successCallback } = payload;
    const formData = prepareFileForRequest(file, 'file');
    formData.append('format', format);

    if (recruitingSeasonId) {
      formData.append('recruiting_season_id', recruitingSeasonId);
    }

    const response = yield call(
      Api.importLedgers,
      formData,
    );

    if (successCallback) {
      yield call(successCallback);
    }

    const { errors, valid_row_count } = Mapper.getLedgerImportResponse(response);

    if (errors.length > 0) {
      yield put(addToastsAction([{
        type: toastType.ERROR,
        message: 'Ledgers import failed.',
        details: errors.join('\n'),
      }]));
    }

    let message = 'Ledgers have been successfully imported.';
    if (valid_row_count > 0) {
      const isSingle = valid_row_count === 1;
      message = `${valid_row_count} ledger${isSingle ? '' : 's'} ${isSingle ? 'has' : 'have'} been successfully imported.`;
    }
    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message,
      details: null,
    }]));
  } catch (error) {
    let { errors = [] } = error?.response?.data ?? {};
    errors = errors
      .map(({ detail: message }) => message)
      .join('\n');

    yield put(addToastsAction([{
      type: toastType.ERROR,
      message: 'Ledgers import failed.',
      details: errors,
    }]));

    throw error;
  }
}

function* getImportLedgersTemplateSaga({ payload }) {
  try {
    const { format } = payload;
    const response = yield call(
      Api.getImportLedgersTemplate,
      { format },
    );

    yield put(importLedgersTemplateAsync.success(response));
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* exportLedgersSaga({ payload }) {
  try {
    const response = yield call(
      Api.exportLedgers,
      { ...payload },
    );

    yield put(exportLedgersAsync.success(response));
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* createLedgerSaga({ payload }) {
  try {
    const { data, successCallback } = payload;
    const response = yield call(
      Api.createLedger,
      Mapper.prepareDataForCreateLedger(data),
    );

    yield put(createLedgerAsync.success(response));

    if (successCallback) {
      yield call(successCallback, response?.data?.[0]?.attributes?.id);
    }
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* editLedgerSaga({ payload }) {
  try {
    const { ledgerRecordId, data, successCallback } = payload;
    const response = yield call(
      Api.updateLedger(ledgerRecordId),
      Mapper.prepareDataForEditLedger(data),
    );

    yield put(editLedgerAsync.success(response));

    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* createNoteSaga({ payload }) {
  try {
    const { data, successCallback } = payload;
    const response = yield call(
      Api.createNote(data.ledgerRecordId),
      { note: data.note },
    );

    yield put(createNoteAsync.success(response?.data?.attributes));

    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* getLedgerNotesSaga({ payload }) {
  try {
    const { ledgerId, successCallback } = payload;
    const response = yield call(
      Api.getLedgerNotes(ledgerId),
      { ...payload },
    );

    yield put(requestLedgerNotesAsync.success(Mapper.getLedgerNotes(response)));

    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* updateOvernightPaymentSaga({ payload }) {
  try {
    const { ledgerId, isOvernightPaymentRequest } = payload;

    yield put(updateOvernightPaymentAsync.success({
      ledgerId: ledgerId, isOvernightPaymentRequest: isOvernightPaymentRequest,
    }));

    yield call(
      Api.updateOvernightPayment(ledgerId),
      { overnight_payment: isOvernightPaymentRequest },
    );
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

export function* ledgerActionWatcher() {
  yield takeLatest(
    requestLedgersAsync.request.type,
    createRequestSaga(requestLedgers, {
      keyNew: 'ledgerList',
      errKey: 'ledgerList',
      write: true,
    }),
  );

  yield takeLatest(
    archiveLedgerAsync.request.type,
    createRequestSaga(archiveLedger, {
      keyNew: 'archiveLedger',
      errKey: 'archiveLedger',
      write: true,
    }),
  );

  yield takeLatest(
    unArchiveLedgerAsync.request.type,
    createRequestSaga(unArchiveLedger, {
      keyNew: 'archiveLedger',
      errKey: 'archiveLedger',
      write: true,
    }),
  );

  yield takeLatest(
    cancelLedgerAsync.request.type,
    createRequestSaga(cancelLedger, {
      keyNew: 'cancelLedger',
      errKey: 'cancelLedger',
      write: true,
    }),
  );

  yield takeLatest(
    importLedgersAsync.request.type,
    createRequestSaga(importLedgersSaga, {
      keyNew: 'importLedgers',
      errKey: 'importLedgers',
      write: true,
    }),
  );

  yield takeLatest(
    importLedgersTemplateAsync.request.type,
    createRequestSaga(getImportLedgersTemplateSaga, {
      keyNew: 'importLedgersTemplate',
      errKey: 'importLedgersTemplate',
      write: true,
    }),
  );

  yield takeLatest(
    exportLedgersAsync.request.type,
    createRequestSaga(exportLedgersSaga, {
      keyNew: 'exportLedgers',
      errKey: 'exportLedgers',
      write: true,
    }),
  );

  yield takeLatest(
    createLedgerAsync.request.type,
    createRequestSaga(createLedgerSaga, {
      keyNew: 'createLedger',
      errKey: 'createLedger',
      write: true,
    }),
  );

  yield takeLatest(
    editLedgerAsync.request.type,
    createRequestSaga(editLedgerSaga, {
      keyNew: 'editLedger',
      errKey: 'editLedger',
      write: true,
    }),
  );

  yield takeLatest(
    createNoteAsync.request.type,
    createRequestSaga(createNoteSaga, {
      keyNew: 'createNote',
      errKey: 'createNote',
      write: true,
    }),
  );

  yield takeLatest(
    requestLedgerHistoryAsync.request.type,
    createRequestSaga(getLedgerHisotry, {
      keyNew: 'ledgerHistory',
      errKey: 'ledgerHistory',
      write: true,
    }),
  );

  yield takeLatest(
    requestLedgerNotesAsync.request.type,
    createRequestSaga(getLedgerNotesSaga, {
      keyNew: 'update',
      errKey: 'ledgerNotes',
      write: true,
    }),
  );

  yield takeLatest(
    updateOvernightPaymentAsync.request.type,
    createRequestSaga(updateOvernightPaymentSaga, {
      keyNew: 'updateOvernightPayment',
      errKey: 'updateOvernightPayment',
      write: true,
    }),
  );
}
